import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const EternalShishyaLogo = ({ size = 28 }) => (
  <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" width={size} height={size}>
    <path d="M20 5C24 2 30 4 32 10C34 16 30 22 26 26C23 29 21 32 20 35C19 32 17 29 14 26C10 22 6 16 8 10C10 4 16 2 20 5Z" fill="#F9B877" />
    <path d="M20 5C24 2 30 4 32 10C34 16 30 22 26 26C23 29 21 32 20 35C19 32 17 29 14 26C10 22 6 16 8 10C10 4 16 2 20 5Z" fill="url(#leafGradientF)" />
    <path d="M20 5C24 2 30 4 32 10C34 16 30 22 26 26C23 29 21 32 20 35C19 32 17 29 14 26C10 22 6 16 8 10C10 4 16 2 20 5Z" stroke="#A67C52" strokeWidth="1.5" fill="none" />
    <path d="M20 5L20 32" stroke="#654733" strokeWidth="1.2" />
    <path d="M20 12C22 14 25 16 27 20" stroke="#7A5639" strokeWidth="0.8" />
    <path d="M20 12C18 14 15 16 13 20" stroke="#7A5639" strokeWidth="0.8" />
    <text x="20" y="22" textAnchor="middle" style={{ fontSize: '9px', fontWeight: 'bold', fontFamily: 'serif', fill: '#543C2E' }}>ॐ</text>
    <defs>
      <linearGradient id="leafGradientF" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#F27516" stopOpacity="0.3" />
        <stop offset="50%" stopColor="#A67C52" stopOpacity="0.1" />
        <stop offset="100%" stopColor="#DB6459" stopOpacity="0.2" />
      </linearGradient>
    </defs>
  </svg>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-earth-900 text-earth-300 mt-16">
      {/* Top divider with saffron accent */}
      <div className="h-1 bg-gradient-to-r from-saffron-600 via-saffron-400 to-terracotta-500" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">

          {/* Brand */}
          <div className="md:col-span-2">
            <Link to="/" className="flex items-center gap-2.5 mb-4">
              <EternalShishyaLogo size={36} />
              <div>
                <span className="text-xl font-serif font-bold text-earth-50">Eternal Shishya</span>
                <div className="text-xs text-saffron-400 font-serif -mt-0.5">शाश्वत शिष्य</div>
              </div>
            </Link>
            <p className="text-earth-400 text-sm leading-relaxed max-w-xs">
              Empowering Semester 6 students with guided, practical project-based learning and recognised completion certificates.
            </p>
            <div className="flex gap-3 mt-5">
              {['Courses', 'Internships'].map(label => (
                <motion.span
                  key={label}
                  whileHover={{ scale: 1.05 }}
                  className="px-3 py-1 rounded-full bg-earth-800 border border-earth-700 text-xs text-earth-300"
                >
                  {label}
                </motion.span>
              ))}
            </div>
          </div>

          {/* Learn */}
          <div>
            <h3 className="text-earth-100 font-semibold text-sm uppercase tracking-widest mb-4">Learn</h3>
            <ul className="space-y-2.5 text-sm">
              {[
                { label: 'Explore Courses', to: '/' },
                { label: 'Internship Projects', to: '/internships' },
                { label: 'My Dashboard', to: '/dashboard' },
              ].map(({ label, to }) => (
                <li key={label}>
                  <Link to={to} className="text-earth-400 hover:text-saffron-400 transition-colors">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Account */}
          <div>
            <h3 className="text-earth-100 font-semibold text-sm uppercase tracking-widest mb-4">Account</h3>
            <ul className="space-y-2.5 text-sm">
              {[
                { label: 'Sign In', to: '/login' },
                { label: 'Create Account', to: '/register' },
              ].map(({ label, to }) => (
                <li key={label}>
                  <Link to={to} className="text-earth-400 hover:text-saffron-400 transition-colors">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="mt-6">
              <h3 className="text-earth-100 font-semibold text-sm uppercase tracking-widest mb-3">Internship Features</h3>
              <ul className="space-y-1.5 text-xs text-earth-500">
                {['15-Hour Guided Projects', 'Per-Task MCQ Quiz', 'Auto Certificate', 'Project Guide Download'].map(f => (
                  <li key={f} className="flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-saffron-500 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-earth-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-xs text-earth-500">
            &copy; {currentYear} Eternal Shishya. All rights reserved.
          </p>
          <p className="text-xs text-earth-600 font-serif italic">
            ज्ञानं परमं बलम् — Knowledge is the ultimate strength
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
